// services/ServiceProviderGalleryService.js - ReactJS (web)
import api from '../api/api';

class ServiceProviderGalleryService {
  // ==================== GALLERY IMAGES ====================

  /**
   * Get all gallery images for a provider
   * GET /api/service-providers/:providerId/gallery
   */
  async getGalleryImages(providerId, params = {}) {
    try {
      const { page = 1, limit = 20 } = params;
      const response = await api.get(`/service-providers/${providerId}/gallery`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get a single gallery image by ID
   * GET /api/service-providers/:providerId/gallery/:imageId
   */
  async getGalleryImageById(providerId, imageId) {
    try {
      const response = await api.get(`/service-providers/${providerId}/gallery/${imageId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add gallery images to a provider (requires auth)
   * POST /api/service-providers/:providerId/gallery
   */
  async addGalleryImages(providerId, files, options = {}) {
    try {
      const formData = new FormData();
      
      // ✅ IMPORTANT: Use 'images' field name to match backend middleware
      // uploadProviderGalleryImages.array('images', 30)
      files.forEach((file, index) => {
        // Handle both React Native style objects and browser File objects
        if (file instanceof File) {
          // Browser File object
          formData.append('images', file);
        } else if (file.uri) {
          // React Native style object - convert for web
          // For web, we need to fetch the file content
          formData.append('images', {
            uri: file.uri,
            type: file.type || 'image/jpeg',
            name: file.name || `image_${index + 1}.jpg`,
          });
        } else {
          // Assume it's already a Blob or File
          formData.append('images', file);
        }
      });

      // Append options
      if (options.captions) {
        formData.append('captions', JSON.stringify(options.captions));
      }

      console.log('[API] 📤 Uploading gallery files:', files.map(f => ({ 
        name: f.name || f.uri, 
        size: f.size, 
        type: f.type 
      })));
      console.log('[API] 📤 FormData options:', { 
        captions: options.captions
      });

      const response = await api.post(
        `/service-providers/${providerId}/gallery`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update gallery image details
   * PUT /api/service-providers/:providerId/gallery/:imageId
   */
  async updateGalleryImage(providerId, imageId, updateData) {
    try {
      const response = await api.put(
        `/service-providers/${providerId}/gallery/${imageId}`,
        updateData
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove gallery image
   * DELETE /api/service-providers/:providerId/gallery/:imageId
   */
  async removeGalleryImage(providerId, imageId) {
    try {
      const response = await api.delete(
        `/service-providers/${providerId}/gallery/${imageId}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove multiple gallery images
   * DELETE /api/service-providers/:providerId/gallery
   */
  async removeGalleryImages(providerId, imageIds) {
    try {
      const response = await api.delete(
        `/service-providers/${providerId}/gallery`,
        { data: { imageIds } }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Reorder gallery images
   * PUT /api/service-providers/:providerId/gallery/reorder
   */
  async reorderGallery(providerId, imageOrder) {
    try {
      const response = await api.put(
        `/service-providers/${providerId}/gallery/reorder`,
        { imageOrder }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete all gallery images for a provider (requires auth)
   * DELETE /api/service-providers/:providerId/gallery/all
   */
  async deleteAllGalleryImages(providerId) {
    try {
      const response = await api.delete(
        `/service-providers/${providerId}/gallery/all`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Handle API errors
   */
  handleError(error) {
    let errorMessage = ' ';
    
    if (error.response?.data?.data?.error?.message) {
      errorMessage = error.response.data.data.error.message;
    } else if (error.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    console.error('[API] ❌ Error:', {
      message: errorMessage,
      status: error.response?.status,
      data: error.response?.data
    });
    
    return {
      success: false,
      error: errorMessage,
      statusCode: error.response?.status,
      data: error.response?.data
    };
  }

  /**
   * Validate image file before upload
   */
  validateImageFile(file) {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp'];
    
    if (!file) {
      return { valid: false, error: 'No file provided' };
    }
    
    if (file.size && file.size > maxSize) {
      return { valid: false, error: 'File size exceeds 10MB limit' };
    }
    
    if (file.type && !allowedTypes.includes(file.type)) {
      return { valid: false, error: `Unsupported file type: ${file.type}` };
    }
    
    return { valid: true };
  }

  /**
   * Validate multiple image files before upload
   */
  validateImageFiles(files) {
    if (!files || files.length === 0) {
      return { valid: false, error: 'No files provided' };
    }

    for (let i = 0; i < files.length; i++) {
      const result = this.validateImageFile(files[i]);
      if (!result.valid) {
        return { valid: false, error: `File ${i + 1}: ${result.error}` };
      }
    }

    return { valid: true };
  }

  /**
   * Get image preview URL (web helper)
   */
  getImagePreviewUrl(file) {
    if (!file) return null;
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }
    if (file.uri) {
      return file.uri;
    }
    return null;
  }

  /**
   * Revoke image preview URL (web helper)
   */
  revokeImagePreviewUrl(url) {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Create FormData from files (web helper)
   */
  createFormData(files, options = {}) {
    const formData = new FormData();
    
    files.forEach((file, index) => {
      if (file instanceof File) {
        formData.append('images', file);
      } else if (file.uri) {
        // For web, we need to fetch the file content
        // This handles React Native style objects
        formData.append('images', {
          uri: file.uri,
          type: file.type || 'image/jpeg',
          name: file.name || `image_${index + 1}.jpg`,
        });
      }
    });

    if (options.captions) {
      formData.append('captions', JSON.stringify(options.captions));
    }
    
    return formData;
  }

  /**
   * Get gallery image count from provider data
   */
  getGalleryCount(provider) {
    if (!provider || !provider.gallery) return 0;
    return provider.gallery.length || 0;
  }

  /**
   * Format gallery image for display
   */
  formatGalleryImage(image) {
    if (!image) return null;
    return {
      id: image._id || image.id,
      url: image.url,
      publicId: image.publicId,
      thumbnail: image.thumbnail,
      caption: image.caption || '',
      uploadedAt: image.uploadedAt || image.createdAt,
    };
  }

  /**
   * Format multiple gallery images for display
   */
  formatGalleryImages(images) {
    if (!Array.isArray(images)) return [];
    return images.map(img => this.formatGalleryImage(img));
  }
}

export default new ServiceProviderGalleryService();